import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  ANNUAL_TRIAL_DAYS,
  PlanOptions,
} from "@bitwarden/web-vault/app/models/types/subscription.type";
import { Router } from "@angular/router";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { HelperTrialRemaining } from "@bitwarden/web-vault/app/shared/utils/helper-trial-remaining";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { AppleConfigService } from "@bitwarden/web-vault/app/services/api/apple-config.service";
import { PaymentService } from "@bitwarden/web-vault/app/services/subscription/payment.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { SubscriptionView } from "@bitwarden/web-vault/app/models/view/subscription/subscription.view";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { AUTO_REDEEM_CREDITS } from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
// GlossRoutes.Credits is the new "Gloss Rewards" page (CreditsComponent).

// Map ISO currency codes (as stored in feeAmount, e.g. "AUD 119.88") to display symbols.
const CURRENCY_SYMBOLS: Record<string, string> = { AUD: "A$" };

// "AUD 119.88" + "/year" -> "A$119.88/year"
function formatPriceWithCycle(feeAmount?: string, paymentCycle?: string): string {
  if (!feeAmount) {
    return "";
  }
  const display = feeAmount.replace(
    /^([A-Z]{3})\s*/,
    (_match, code: string) => CURRENCY_SYMBOLS[code] ?? `${code} `,
  );
  return `${display}${paymentCycle ?? ""}`;
}

function hasHadTrialOrPaidPlan(subscription: SubscriptionView | null | undefined): boolean {
  if (!subscription) {
    return false;
  }
  const trialStart = subscription.trialStartDate;
  return subscription.isPremium || (trialStart instanceof Date && !isNaN(trialStart.getTime()));
}

// Unknown (not loaded / history null) counts as "no"; a loaded empty history is eligible (#6034).
function canStartFreeTrial(
  active: SubscriptionView | null | undefined,
  history: SubscriptionView[] | null | undefined,
): boolean {
  if (!active || history == null) {
    return false;
  }
  return ![active, ...history].some(hasHadTrialOrPaidPlan);
}

@Component({
  selector: "app-plan-details-dialog",
  templateUrl: "./plan-details-dialog.component.html",
})
export class PlanDetailsDialogComponent implements OnInit {
  private i18nService = inject(I18nService);
  private confirmationService = inject(ConfirmationDialogService);
  private userStoreService = inject(UserStoreService);
  private appleConfigService = inject(AppleConfigService);
  private router = inject(Router);
  private creditsService = inject(CreditsService);
  private paymentService = inject(PaymentService);
  private appState = inject(AppStateStoreService);
  private dialogFactory = new DialogFactory();

  // On Apple devices Stripe is not allowed: subscriptions are redeemed with credits on the Rewards page.
  protected readonly isApple = this.appleConfigService.iOSIAPCapability;

  // Only the monthly redemption exists; the annual one is not implemented yet.
  protected readonly canRedeemWithCredits = computed(
    () =>
      this.selectedBillingCycle() === "monthly" &&
      this.creditsService.totalGoldCredits() >= AUTO_REDEEM_CREDITS,
  );

  // The 14-day trial is free (no card, no credits) and once per user; gloss-api enforces it too.
  protected readonly canStartAppleTrial = computed(() =>
    canStartFreeTrial(
      this.userStoreService.subscription.activeSubscription(),
      this.userStoreService.user.subscriptions(),
    ),
  );

  // The global overlay is latched off post-login, so the dialog renders its own while granting.
  protected readonly processingTrial = signal(false);

  protected readonly appleActionText = computed(() => {
    if (this.selectedBillingCycle() !== "monthly") {
      return "Annual redemption coming soon";
    }
    return this.canRedeemWithCredits() ? "Redeem 1 month with credits" : "Not enough credits";
  });

  @ViewChild("confirmationTemplate", { static: false }) confirmationTemplate?: TemplateRef<any>;

  closeDialog = input.required<() => void>();

  // For plan cards (annual/monthly)
  planType = input<"annual" | "monthly">();
  marketLabel = input<string>("");
  showSwitchButton = input<boolean>(false);
  showSubscribeButton = input<boolean>(false);
  onAnnualAction = input<((withCard?: boolean) => void) | (() => void) | undefined>();
  onMonthlyAction = input<((withCard?: boolean) => void) | (() => void) | undefined>();
  onCancelSubscription = input<(() => void) | undefined>();
  // Payment method to start on (set when reopened after cancelling the change-plan dialog).
  initialPaymentMethod = input<"add" | "skip">();

  // Switch button for annual/monthly selection
  // Store user's manual selection (null means use the input planType)
  private _manuallySelectedCycle = signal<"annual" | "monthly" | null>(null);

  // The actual selected billing cycle - uses manual selection if set, otherwise uses planType input
  protected selectedBillingCycle = computed(() => {
    const manual = this._manuallySelectedCycle();
    if (manual !== null) {
      return manual;
    }
    const type = this.planType();
    return type || "annual";
  });

  // Payment method selection
  protected selectedPaymentMethod = signal<"add" | "skip">("skip");

  protected readonly planOptions = PlanOptions;
  protected readonly trialDays = ANNUAL_TRIAL_DAYS;

  // Plugins included in the bundle; the count badge is derived from this list.
  protected readonly includedPlugins = [
    {
      icon: "retrieve-bundle",
      iconBgClass: "tw-bg-color-surface-elevated",
      name: "Auto Retrieve",
      by: "By Fiskil",
      description:
        "Provides highly secure, API-only bank connections. It delivers a fast, seamless, and fully compliant data fetching experience.",
    },
    {
      icon: "folder-bundle",
      iconBgClass: "tw-bg-dt-jade-9/10",
      name: "File Storage",
      by: "By GLOSS",
      description: "1GB Financial Vault (Stores approx. 5,000+ bank statements)",
    },
  ];

  // Price of the selected billing cycle formatted in the design style, sourced from plan data.
  protected afterTrialPriceDisplay = computed(() => {
    const type = this.selectedBillingCycle();
    const plan =
      type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;
    return formatPriceWithCycle(plan.feeAmount, plan.paymentCycle);
  });

  // Trial helper for calculating remaining trial days
  private trialHelper = new HelperTrialRemaining();

  // For confirmation message display
  protected confirmationPlanLabel = "";
  protected confirmationBillingAmount = "";
  protected confirmationBillingPeriod = "";

  // Computed values that auto-lookup from plan objects
  protected planLabel = computed(() => {
    const type = this.selectedBillingCycle();

    const plan =
      type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;
    const market = this.marketLabel();

    const translatedLabel = this.i18nService.t(plan.label);
    return market ? `${market} (${translatedLabel})` : translatedLabel;
  });

  protected youSave = computed(() => {
    const type = this.selectedBillingCycle();
    return type === "annual" ? this.planOptions.premiumAnnual.youSave || "" : "";
  });

  protected priceAmount = computed(() => {
    const type = this.selectedBillingCycle();
    const plan =
      type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;
    return plan.feeAmount;
  });

  protected paymentCycle = computed(() => {
    const type = this.selectedBillingCycle();
    const plan =
      type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;
    return plan.paymentCycle;
  });

  protected description = computed(() => {
    const type = this.selectedBillingCycle();
    return type === "annual" ? "yearlyDescription" : "monthlyDescription";
  });

  protected monthlyEquivalent = computed(() => {
    const type = this.selectedBillingCycle();
    if (type !== "annual") {
      return "";
    }

    const plan = this.planOptions.premiumAnnual;
    // Extract numeric value from feeAmount (e.g., "AUD 119.88" -> 119.88)
    const match = plan.feeAmount.match(/[\d.]+/);
    if (match) {
      const annualPrice = parseFloat(match[0]);
      const monthlyPrice = (annualPrice / 12).toFixed(2);
      // Get currency prefix (e.g., "AUD ")
      const currencyPrefix = plan.feeAmount.split(match[0])[0];
      return `${currencyPrefix}${monthlyPrice}`;
    }
    return "";
  });

  // Check if user has premium subscription
  // activeSubscription() is null before it loads and after logout; null means not premium.
  protected hasPremiumSubscription = computed(() => {
    return this.userStoreService.subscription.activeSubscription()?.isPremium ?? false;
  });

  // Check if the selected billing cycle matches current subscription
  protected isSelectedCycleCurrentBundle = computed(() => {
    if (!this.hasPremiumSubscription()) {
      return false;
    }
    const currentCycle = this.userStoreService.subscription.activeSubscription().paymentCycle;
    const selectedCycle = this.selectedBillingCycle();
    return (
      (selectedCycle === "annual" && currentCycle === "year") ||
      (selectedCycle === "monthly" && currentCycle === "month")
    );
  });

  // Check if this is the "Your Bundle" view (no actions available)
  protected isYourBundleView = computed(() => {
    return !this.showSwitchButton() && !this.showSubscribeButton();
  });

  // Trial / billing display values (shared logic in HelperTrialRemaining)
  protected trialRemainingDays = computed(() =>
    this.trialHelper.getTrialRemainingDays(this.userStoreService.subscription.activeSubscription()),
  );

  protected isInTrialPeriod = computed(() =>
    this.trialHelper.isInTrialPeriod(this.userStoreService.subscription.activeSubscription()),
  );

  protected nextBillingDate = computed(() =>
    this.trialHelper.getNextBillingDate(this.userStoreService.subscription.activeSubscription()),
  );

  protected formattedBillingDate = computed(() =>
    this.trialHelper.formatBillingDate(this.nextBillingDate()),
  );

  ngOnInit(): void {
    // Inputs aren't set while fields are initialised, so seed the selection here.
    const initialMethod = this.initialPaymentMethod();
    if (initialMethod) {
      this.selectedPaymentMethod.set(initialMethod);
    }
  }

  protected selectBillingCycle(cycle: "annual" | "monthly"): void {
    this._manuallySelectedCycle.set(cycle);
  }

  protected selectPaymentMethod(method: "add" | "skip"): void {
    this.selectedPaymentMethod.set(method);
  }

  protected close(): void {
    this.closeDialog()();
  }

  private redirectToRewards(): void {
    this.close();
    void this.router.navigate(["/", GlossRoutes.Credits]);
  }

  protected async startAppleTrial(): Promise<void> {
    if (this.processingTrial()) {
      return;
    }
    this.processingTrial.set(true);
    this.appState.startProcess("general", "Getting free trial...");
    try {
      await this.paymentService.autoGetFreeTrial(this.selectedBillingCycle());
    } catch {
      return; // already surfaced by autoGetFreeTrial; stay open to retry
    } finally {
      this.appState.endProcess("general");
      this.processingTrial.set(false);
    }
    this.close();
  }

  protected async handleAction(): Promise<void> {
    if (this.isApple) {
      this.redirectToRewards();
      return;
    }

    const type = this.selectedBillingCycle();
    const actionFn = type === "annual" ? this.onAnnualAction() : this.onMonthlyAction();

    if (!actionFn) {
      return;
    }

    // If this is a switch scenario (user has premium), use the change-plan dialog
    if (this.showSwitchButton()) {
      // Save the current dialog config to reopen it on cancel
      const reopenPlanDetails = () => {
        this.dialogFactory.openPlanDetailsDialog({
          planType: type,
          marketLabel: this.marketLabel(),
          showSwitchButton: this.showSwitchButton(),
          showSubscribeButton: this.showSubscribeButton(),
          onAnnualAction: this.onAnnualAction(),
          onMonthlyAction: this.onMonthlyAction(),
          onCancelSubscription: this.onCancelSubscription(),
          initialPaymentMethod: this.selectedPaymentMethod(),
        });
      };

      // Close current dialog first
      this.close();

      // Then open change-plan dialog
      this.dialogFactory.openChangePlanDialog({
        newPlanType: type,
        selectedPaymentMethod: this.selectedPaymentMethod(),
        marketLabel: this.marketLabel(),
        onConfirm: () => {
          // Pass the payment method choice: 'add' = true (with card), 'skip' = false
          const withCard = this.selectedPaymentMethod() === "add";
          actionFn(withCard);
        },
        onCancel: reopenPlanDetails,
      });
    } else {
      // For subscribe scenario (no premium), use the confirmation dialog
      const plan =
        type === "annual" ? this.planOptions.premiumAnnual : this.planOptions.premiumMonthly;

      this.confirmationPlanLabel = this.planLabel();
      this.confirmationBillingAmount = plan.feeAmount;
      this.confirmationBillingPeriod = type === "annual" ? "year" : "month";

      const confirmed = await this.confirmationService.confirmFor(
        ConfirmationEnum.confirmingBundleSwitch,
        { templateContent: this.confirmationTemplate },
      );

      if (confirmed) {
        actionFn(this.selectedPaymentMethod() === "add");
        this.close();
      }
    }
  }

  protected handleCancelSubscription(): void {
    const cancelFn = this.onCancelSubscription();
    if (cancelFn) {
      cancelFn();
    }
  }
}
